// Format cheatsheet box hiển thị slash commands của team-ai-pack sau khi
// `avatar init` thành công. Mục đích: user mới biết ngay những lệnh cốt lõi
// có thể gõ trong Claude Code để dùng pack.
//
// v3.8.0: ĐỌC ĐỘNG từ .claude/pack/commands/avatar/*.md (frontmatter description)
// thay vì hardcode — cheatsheet LUÔN khớp pack, không lệch lại khi pack đổi
// command (bug cũ: hardcode 13 lệnh, miss /avatar:savecost:* sau khi pack lên 50).
//
// Chỉ hiển thị command GỐC (top-level, vd /avatar:plan) — bỏ sub-command
// (/avatar:plan:fast, :hard, :auto...) để box gọn. Catalog đầy đủ ở footer.
import type { Dirent } from "node:fs";
import { readdir, readFile } from "node:fs/promises";
import { join } from "node:path";
import boxen from "boxen";
import { chalk } from "./terminal-logger.js";

interface CommandEntry {
  cmd: string;
  desc: string;
}

// Parse `description:` từ frontmatter YAML đơn giản (block --- ... ---).
// KHÔNG full YAML parser (KISS) — chỉ bắt dòng `description: ...` đầu tiên.
function extractDescription(markdown: string): string {
  const fmMatch = markdown.match(/^---\n([\s\S]*?)\n---/);
  if (!fmMatch?.[1]) return "";
  const descLine = fmMatch[1].split("\n").find((l) => l.trimStart().startsWith("description:"));
  if (!descLine) return "";
  return descLine
    .replace(/^\s*description:\s*/, "")
    .replace(/^['"]|['"]$/g, "")
    .trim();
}

// Đọc lệnh cho cheatsheet: file .md GỐC (vd /avatar:plan) + các NHÓM sub-command
// KHÔNG có lệnh gốc cùng tên (vd savecost/, git/ — chỉ tồn tại dạng thư mục).
// Bỏ sub-command lẻ (:fast/:hard) để box gọn. Trả [] nếu thiếu dir/lỗi đọc
// (fail-mềm: init summary vẫn hiển thị phần khác, chỉ bỏ cheatsheet).
async function readTopLevelCommands(avatarCommandsDir: string): Promise<CommandEntry[]> {
  let entries: Dirent[];
  try {
    entries = await readdir(avatarCommandsDir, { withFileTypes: true });
  } catch {
    return [];
  }
  const out: CommandEntry[] = [];
  const topLevelNames = new Set<string>();

  // (1) Lệnh gốc — file .md trực tiếp.
  for (const entry of entries) {
    if (!entry.isFile() || !entry.name.endsWith(".md")) continue;
    const name = entry.name.replace(/\.md$/, "");
    topLevelNames.add(name);
    try {
      const content = await readFile(join(avatarCommandsDir, entry.name), "utf8");
      out.push({ cmd: `/avatar:${name}`, desc: extractDescription(content) });
    } catch {
      // Bỏ qua file đọc lỗi — không chặn cả cheatsheet.
    }
  }

  // (2) Nhóm sub-command KHÔNG có lệnh gốc cùng tên (vd savecost, git, review):
  // hiện 1 dòng "nhóm" + đếm số lệnh con, để user biết nhóm tồn tại.
  for (const entry of entries) {
    if (!entry.isDirectory() || topLevelNames.has(entry.name)) continue;
    const subDir = join(avatarCommandsDir, entry.name);
    const subCount = await countAllCommands(subDir);
    if (subCount === 0) continue;
    out.push({
      cmd: `/avatar:${entry.name}:*`,
      desc: `nhóm ${subCount} lệnh (gõ /avatar:${entry.name}: để xem)`,
    });
  }

  out.sort((a, b) => a.cmd.localeCompare(b.cmd));
  return out;
}

// Đếm TỔNG số command (gồm sub-command đệ quy) cho footer "catalog đầy đủ N".
async function countAllCommands(avatarCommandsDir: string): Promise<number> {
  let total = 0;
  let entries: Dirent[];
  try {
    entries = await readdir(avatarCommandsDir, { withFileTypes: true });
  } catch {
    return 0;
  }
  for (const entry of entries) {
    if (entry.isDirectory()) {
      total += await countAllCommands(join(avatarCommandsDir, entry.name));
    } else if (entry.name.endsWith(".md")) {
      total += 1;
    }
  }
  return total;
}

// Build cheatsheet box từ pack thật. packDir = <workspace>/.claude/pack.
// Trả null nếu không đọc được command nào (caller skip in box).
export async function formatPackCommandsCheatsheetBox(packDir: string): Promise<string | null> {
  const avatarCommandsDir = join(packDir, "commands", "avatar");
  const commands = await readTopLevelCommands(avatarCommandsDir);
  if (commands.length === 0) return null;

  const total = await countAllCommands(avatarCommandsDir);
  const maxCmdWidth = Math.max(...commands.map((e) => e.cmd.length));

  const header = chalk.bold("🎯 Slash commands từ team-ai-pack");
  const subheader = chalk.dim("Gõ trong Claude Code session để gọi capability của pack:");

  const lines = commands.map((e) => {
    const cmdPadded = chalk.cyan(e.cmd.padEnd(maxCmdWidth));
    return e.desc ? `  ${cmdPadded}  ${chalk.dim(e.desc)}` : `  ${cmdPadded}`;
  });

  const footer = chalk.dim(
    `${commands.length} lệnh gốc (đầy đủ ${total} gồm sub-command). ` +
      "Sub: thêm :fast / :hard / :auto / :parallel sau lệnh gốc.",
  );

  const content = [header, subheader, "", ...lines, "", footer].join("\n");

  return boxen(content, {
    padding: 1,
    borderStyle: "round",
    borderColor: "cyan",
  });
}
